import React, { useEffect, useRef, useState } from 'react';
import {
  TRAILER_POSTER_SRC,
  TRAILER_POSTER_SRC_SMALL,
  TRAILER_VIDEO_ID,
  TRAILER_VIDEO_LENGTH_LABEL,
} from './courseContent';
import './BootcampTrailer.css';

const PLAYER_ORIGIN = 'https://www.youtube-nocookie.com';

/**
 * The Bootcamp trailer, placed right under the hero on every Bootcamp page arm
 * (Rob, 2026-09-24). Same playback pattern as PreviewVideo: the player is
 * mounted behind the poster, and the poster tap sends YouTube a playVideo
 * command. iOS only honours that when the iframe already exists at the moment
 * of the tap; mounting the iframe on tap made iPhone visitors tap twice.
 *
 * It has its own class names and test id so pages that count the class
 * preview's play button still find exactly one. No duration badge on the
 * poster: the Clarity pages ban pills and badges, and the heading already
 * says how long it is.
 */
const BootcampTrailer = ({ onPlay, onComplete, className = '' }) => {
  const [playing, setPlaying] = useState(false);
  const iframeRef = useRef(null);
  const completedRef = useRef(false);

  const listenForPlayerEvents = () => {
    const playerWindow = iframeRef.current?.contentWindow;
    if (!playerWindow) return;
    const id = `bootcamp-trailer-${TRAILER_VIDEO_ID}`;
    playerWindow.postMessage(JSON.stringify({ event: 'listening', id }), PLAYER_ORIGIN);
    playerWindow.postMessage(
      JSON.stringify({
        event: 'command',
        func: 'addEventListener',
        args: ['onStateChange'],
        id,
      }),
      PLAYER_ORIGIN,
    );
  };

  useEffect(() => {
    if (playing) iframeRef.current?.focus();
  }, [playing]);

  useEffect(() => {
    if (!playing || !onComplete) return undefined;
    const handlePlayerMessage = (event) => {
      if (event.origin !== PLAYER_ORIGIN && event.origin !== 'https://www.youtube.com') return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }
      const ended = payload?.event === 'onStateChange' && payload?.info === 0;
      const currentTime = Number(payload?.info?.currentTime);
      const duration = Number(payload?.info?.duration);
      const watchedToEnd = duration > 0 && currentTime / duration >= 0.95;
      if (!completedRef.current && (ended || watchedToEnd)) {
        completedRef.current = true;
        onComplete();
      }
    };
    window.addEventListener('message', handlePlayerMessage);
    return () => window.removeEventListener('message', handlePlayerMessage);
  }, [onComplete, playing]);

  const startPlayback = () => {
    onPlay?.();
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
      PLAYER_ORIGIN,
    );
    setPlaying(true);
  };

  return (
    <section
      className={`bt-trailer${className ? ` ${className}` : ''}`}
      aria-label="AI Trading Bootcamp trailer"
      data-testid="bootcamp-trailer"
    >
      <div className="bt-trailer-inner">
        <h2 className="bt-trailer-heading">
          Watch the <span className="bt-trailer-heading-nowrap">2-minute</span> trailer
        </h2>
        <div className="bt-trailer-frame">
          <iframe
            ref={iframeRef}
            src={`${PLAYER_ORIGIN}/embed/${TRAILER_VIDEO_ID}?rel=0&modestbranding=1&enablejsapi=1&playsinline=1`}
            title="AI Trading Bootcamp trailer"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
            tabIndex={playing ? 0 : -1}
            onLoad={listenForPlayerEvents}
          />
          {playing ? null : (
            <button
              type="button"
              className="bt-trailer-poster"
              onClick={startPlayback}
              aria-label={`Play the AI Trading Bootcamp trailer (${TRAILER_VIDEO_LENGTH_LABEL})`}
              data-testid="trailer-play"
            >
              <img
                src={TRAILER_POSTER_SRC}
                srcSet={`${TRAILER_POSTER_SRC_SMALL} 640w, ${TRAILER_POSTER_SRC} 1280w`}
                sizes="(max-width: 700px) 100vw, 960px"
                alt=""
                width="1280"
                height="720"
                decoding="async"
              />
              <span className="bt-trailer-play" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default BootcampTrailer;
